import { useState } from "react";
import { lookupSlug } from "@/lib/slugLookup";
import { Navigate } from "@/lib/router-compat";
import { useStableLocation } from "@/hooks/useStableLocation";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import CommercialBrowseSidebar from "@/components/services/CommercialBrowseSidebar";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import ContactCTA from "@/components/ContactCTA";
import CirAccreditationBlock from "@/components/services/CirAccreditationBlock";
import WebinarRegistrationForm from "@/components/webinar/WebinarRegistrationForm";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getServiceBySlug } from "@/data/servicesData";
import ServiceHero from "@/components/services/ServiceHero";
import ServiceWhyQbd from "@/components/services/ServiceWhyQbd";
import ServiceRelatedContent from "@/components/services/ServiceRelatedContent";
import GenericOfferingContent from "@/components/services/GenericOfferingContent";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import { heroTakeaway, keyTakeawaySchema } from "@/data/keyTakeaways";

import { labOfferingData } from "@/data/labOfferingData";
import { useLanguage, langPrefix } from "@/hooks/useLanguage";
import { getLabOfferingI18n } from "@/i18n/labOfferings";
import { getServiceContent } from "@/i18n/serviceDetail";
import { localizeHref } from "@/lib/localizeHref";

import { SITE_URL as SITE, DEFAULT_OG_IMAGE } from "@/config/site";

/* Map Spanish URL slugs → English content keys */
const ES_SLUG_MAP: Record<string, string> = {
  "analisis-quimico": "chemical-analysis",
  "desarrollo-metodos": "method-development",
  "validacion-metodo": "method-validation",
  "estudios-estabilidad": "stability-studies",
  "validacion-limpieza": "cleaning-validation",
  "ensayos-de-disolventes-residuales-gc": "residual-solvent-testing-gc",
  "analisis-microbiologico": "microbiological-analysis",
  "impurezas-elementales": "elemental-impurities",
  "liberación-de-lotes-qp": "qp-release-and-eu-import",
  "monitorizacion-medioambiental": "environmental-monitoring",
  "proyectos-id": "rd-projects",
};

const offeringPages: Record<string, {
  title: string;
  subtitle: string;
  description: string;
  contentKey: string;
  ogImage?: string;
  contentTags?: string[];
  faqItems?: { q: string; a: string }[];
}> = {
  "/en/services/lab-services/chemical-analysis": {
    title: "QC Chemical Analysis",
    subtitle: "Comprehensive analytical testing for your pharmaceutical needs",
    description: "QbD Group's GMP-certified QC laboratory provides reliable and accurate chemical analysis services for raw materials, APIs, IMPs, and finished medicinal products.",
    contentKey: "chemical-analysis",
    contentTags: ["Lab Services", "Pharma & Biotech"],
  },
  "/en/services/lab-services/method-development": {
    title: "Method Development",
    subtitle: "Tailored Solutions for Accurate Analytical Procedures",
    description: "QbD Group combines deep scientific expertise with innovative approaches to design robust analytical methods tailored to your specific needs.",
    contentKey: "method-development",
    contentTags: ["Lab Services"],
  },
  "/en/services/lab-services/method-validation": {
    title: "Method Validation",
    subtitle: "Ensuring reliable and compliant analytical procedures",
    description: "QbD Group ensures your analytical methods align with global standards such as ICH Q2(R1) and compendial guidelines, delivering accurate, reproducible results.",
    contentKey: "method-validation",
    contentTags: ["Lab Services", "Qualification & Validation"],
  },
  "/en/services/lab-services/stability-studies": {
    title: "Stability Studies",
    subtitle: "Protect the integrity and shelf-life of your products",
    description: "QbD Group ensures your products meet the highest quality standards through rigorous ICH-compliant stability studies with state-of-the-art facilities.",
    contentKey: "stability-studies",
    contentTags: ["Lab Services", "Pharma & Biotech"],
  },
  "/en/services/lab-services/cleaning-validation": {
    title: "Cleaning Validation",
    subtitle: "Effective cleaning, safeguarding patient safety",
    description: "QbD Group helps you define and implement best practices for cleaning validation, ensuring compliance with industry standards and regulatory requirements.",
    contentKey: "cleaning-validation",
    contentTags: ["Lab Services", "Qualification & Validation"],
  },
  "/en/services/lab-services/residual-solvent-testing-gc": {
    title: "Residual Solvent Testing (GC)",
    subtitle: "Reliable, GMP-aligned analysis for APIs and finished products",
    description: "Residual solvent testing identifies and quantifies trace organic solvents after manufacturing using validated GC-FID and GC-MS methods, ensuring compliance with ICH Q3C.",
    contentKey: "residual-solvent-testing-gc",
    contentTags: ["Lab Services", "Pharma & Biotech"],
  },
  "/en/services/lab-services/microbiological-analysis": {
    title: "Microbiological Services",
    subtitle: "Comprehensive microbiological testing for patient safety",
    description: "QbD Group provides expert microbiological testing and validation services to ensure the purity and safety of your raw materials and finished products.",
    contentKey: "microbiological-analysis",
    contentTags: ["Lab Services"],
  },
  "/en/services/lab-services/elemental-impurities": {
    title: "Elemental Impurities",
    subtitle: "Ensuring compliance with ICH Q3D Guidelines",
    description: "QbD Group provides expert support for analyzing, controlling, and mitigating elemental impurities in accordance with ICH Q3D guidelines.",
    contentKey: "elemental-impurities",
    contentTags: ["Lab Services", "Toxicology"],
  },
  "/en/services/lab-services/qp-release-and-eu-import": {
    title: "QP Batch Release",
    subtitle: "Reliable GMP batch testing and EU batch release",
    description: "QbD Group provides comprehensive Qualified Person (QP) batch release services, ensuring your medicinal products meet stringent EU regulatory standards.",
    contentKey: "qp-release-and-eu-import",
    contentTags: ["Lab Services", "Quality Assurance"],
  },
  "/en/services/lab-services/environmental-monitoring": {
    title: "Environmental Monitoring",
    subtitle: "Reliable monitoring for contamination control",
    description: "QbD Group specializes in environmental monitoring to help you maintain cleanroom compliance and ensure the integrity of your manufacturing processes.",
    contentKey: "environmental-monitoring",
    contentTags: ["Lab Services", "Qualification & Validation"],
  },
  "/en/services/lab-services/rd-projects": {
    title: "R&D Projects",
    subtitle: "Customized Solutions for Pharma Research and Development",
    description: "QbD Group leverages deep scientific expertise and cutting-edge technologies to support pharmaceutical R&D projects with tailored solutions.",
    contentKey: "rd-projects",
    contentTags: ["Lab Services", "Pharma & Biotech"],
  },
};

/** Resolve the current pathname to a page entry (works for EN and ES routes) */
function resolvePageEntry(pathname: string) {
  // Direct English match
  const directMatch = offeringPages[pathname];
  if (directMatch) return directMatch;

  const lastSegment = pathname.split("/").pop() || "";

  // Chinese route: same slug as English
  if (pathname.startsWith("/zh-cn/")) {
    const enPath = `/en/services/lab-services/${lastSegment}`;
    if (offeringPages[enPath]) return offeringPages[enPath];
  }

  // Spanish route: extract last segment and map
  const enKey = lookupSlug(ES_SLUG_MAP, lastSegment);
  if (enKey) {
    const enPath = `/en/services/lab-services/${enKey}`;
    return offeringPages[enPath];
  }
  return undefined;
}

const LabOffering = () => {
  const location = useStableLocation();
  const [formOpen, setFormOpen] = useState(false);
  const lang = useLanguage();
  const prefix = langPrefix(lang);
  const labServicesPath = localizeHref("/en/services/lab-services", lang);

  const page = resolvePageEntry(location.pathname);
  const parentService = getServiceBySlug("lab-services");

  if (!page || !parentService) return <Navigate to={labServicesPath} replace />;

  // Merge translated service-level content
  const parentI18n = getServiceContent("lab-services", lang);
  const displayParent = parentI18n
    ? { ...parentService, title: parentI18n.title, subtitle: parentI18n.subtitle, description: parentI18n.description, formHeading: parentI18n.formHeading, formSubtitle: parentI18n.formSubtitle, whyQbd: { ...parentService.whyQbd, intro: parentI18n.whyQbd.intro, bullets: parentI18n.whyQbd.bullets } }
    : parentService;

  // Merge translated offering content
  const i18n = getLabOfferingI18n(page.contentKey, lang);

  const displayTitle = i18n?.title ?? page.title;
  const displaySubtitle = i18n?.subtitle ?? page.subtitle;
  const displayDescription = i18n?.description ?? page.description;
  const displayFaqItems = i18n?.faqItems ?? page.faqItems;

  const heroService = {
    ...displayParent,
    title: displayTitle,
    subtitle: displaySubtitle,
    description: displayDescription,
    path: location.pathname,
  };

  
  const pageOgImage = page.ogImage || DEFAULT_OG_IMAGE;
  const metaTitle = `${displayTitle} — ${"Lab Services"} | QbD Group`;
  const metaDesc = displayDescription.slice(0, 155);
  const pageUrl = `${SITE}${location.pathname}`;
  const relatedTags = page.contentTags || ["Lab Services"];

  const data = labOfferingData[page.contentKey];

  // Build display data with i18n overrides for GenericOfferingContent
  const displayData = data && i18n
    ? {
        ...data,
        whatTitle: i18n.whatTitle,
        whatParagraphs: i18n.whatParagraphs,
        whyTitle: i18n.whyTitle,
        whyBenefits: data.whyBenefits.map((b, idx) =>
          i18n.whyBenefits[idx] ? { ...b, title: i18n.whyBenefits[idx].title, text: i18n.whyBenefits[idx].text } : b
        ),
        whyIntro: i18n.whyIntro ?? data.whyIntro,
        helpCards: {
          ...data.helpCards,
          heading: i18n.helpCards.heading,
          intro: i18n.helpCards.intro,
          cards: data.helpCards!.cards.map((c, idx) =>
            i18n.helpCards.cards[idx] ? { ...c, title: i18n.helpCards.cards[idx].title, text: i18n.helpCards.cards[idx].text } : c
          ),
        },
        processTitle: i18n.processTitle,
        processIntro: i18n.processIntro,
        processSteps: data.processSteps!.map((s, idx) =>
          i18n.processSteps[idx] ? { ...s, title: i18n.processSteps[idx].title, text: i18n.processSteps[idx].text } : s
        ),
        partnerTitle: i18n.partnerTitle,
        partnerIntro: i18n.partnerIntro,
        partnerBullets: i18n.partnerBullets,
      }
    : data;

  const parentCrumbLabel = "Lab Services";
  const parentCrumbTo = labServicesPath;
  const servicesLabel = lang === "es" ? "Servicios" : lang === "zh-cn" ? "服务" : "Services";
  const servicesPath = localizeHref("/en/services", lang);

  return (
    <>
      <SEOHead
        title={metaTitle}
        description={metaDesc}
        canonical={pageUrl}
        ogImage={pageOgImage}
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: displayTitle,
            description: displayDescription,
            provider: { "@type": "Organization", name: "QbD Group", url: SITE },
            url: pageUrl,
            isPartOf: { "@type": "Service", name: displayParent.title, url: `${SITE}${parentCrumbTo}` },
            ...keyTakeawaySchema(heroTakeaway(displaySubtitle, displayDescription)),
          })}
        </script>
        {displayFaqItems && displayFaqItems.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: displayFaqItems.map((faq) => ({
                "@type": "Question",
                name: faq.q,
                acceptedAnswer: { "@type": "Answer", text: faq.a },
              })),
            })}
          </script>
        )}
      </SEOHead>

      
      <BreadcrumbJsonLd
        items={[
          { name: servicesLabel, path: servicesPath },
          { name: parentCrumbLabel, path: parentCrumbTo },
          { name: displayTitle },
        ]}
      />

      <ScrollToTop />
      <Navbar />
      <CommercialBrowseSidebar />

      <main className="pb-16 sm:pb-0" style={{ '--service-color': parentService.color } as React.CSSProperties}>
        <ServiceHero
          service={heroService}
          onCtaClick={() => setFormOpen(true)}
          hideDiscoverButton
          parentCrumbs={[
            { label: parentCrumbLabel, to: parentCrumbTo },
          ]}
        />

        {displayData && <GenericOfferingContent data={displayData} onCtaClick={() => setFormOpen(true)} hidePartnerSection />}

        <ServiceWhyQbd
          service={displayParent}
          onCtaClick={() => setFormOpen(true)}
          partnerOverride={displayData ? { intro: displayData.partnerIntro, bullets: displayData.partnerBullets } : undefined}
        />

        {displayFaqItems && displayFaqItems.length > 0 && (
          <section className="py-16 sm:py-20 bg-muted/30">
            <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-6 h-0.5 rounded-full" style={{ backgroundColor: parentService.color }} />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {({ en: "GMP testing requirements", es: "Requisitos de ensayos GMP", "zh-cn": "GMP检测要求" }[lang])}
                </p>
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-6">
                {lang === "es" ? "Preguntas frecuentes" : lang === "zh-cn" ? "常见问题" : "Frequently Asked Questions"}
              </h2>
              <Accordion type="single" collapsible className="space-y-2">
                {displayFaqItems.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="bg-background border border-border/40 rounded-lg px-5">
                    <AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

        <ServiceRelatedContent service={parentService} contentTags={relatedTags} />

        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogTitle className="sr-only">{displayParent.formHeading}</DialogTitle>
            <WebinarRegistrationForm
              registrationUrl={null}
              title={displayParent.title}
              hubspotFormId={parentService.hubspotFormId}
              heading={displayParent.formHeading}
              subtitle={displayParent.formSubtitle}
            />
          </DialogContent>
        </Dialog>

        {page.contentKey === "rd-projects" && <CirAccreditationBlock />}

        <ContactCTA
          onContactClick={() => setFormOpen(true)}
          title={displayParent.formHeading}
          description={displayParent.formSubtitle}
        />
      </main>

      <Footer />
    </>
  );
};

export default LabOffering;
